export const TILE_SIZE = 32;
export const MAP_COLS = 64;
export const MAP_ROWS = 84;
export const MAP_WIDTH = MAP_COLS * TILE_SIZE;
export const MAP_HEIGHT = MAP_ROWS * TILE_SIZE;
function toPixels(t) {
    return {
        x: t.col * TILE_SIZE,
        y: t.row * TILE_SIZE,
        w: t.cols * TILE_SIZE,
        h: t.rows * TILE_SIZE,
    };
}
/**
 * The ten named areas, laid out as a spine.
 *
 * Memory Lane runs down the middle from the stage to the gate, and every other
 * zone opens off it through a single three-tile doorway. That shape is
 * deliberate: a fan who only ever walks straight passes the photo timeline and
 * the entrance to all seven rooms, so nothing has to be discovered on a map
 * nobody will open on a phone.
 *
 * Zones are walkable regions, not colliders. Order matters only where rects
 * touch at an edge — `zoneAt` returns the first hit.
 */
export const ZONES = [
    {
        id: "stage",
        label: "Main Stage",
        rect: toPixels({ col: 4, row: 13, cols: 56, rows: 10 }),
        color: 0x342450,
    },
    {
        id: "prayer",
        label: "Memory Gallery",
        rect: toPixels({ col: 2, row: 24, cols: 24, rows: 13 }),
        color: 0x1f3330,
    },
    {
        id: "theatre",
        label: "Memories Theatre",
        rect: toPixels({ col: 38, row: 24, cols: 24, rows: 13 }),
        color: 0x241f3a,
    },
    {
        id: "phool",
        label: "Phool Arpan",
        rect: toPixels({ col: 2, row: 38, cols: 24, rows: 13 }),
        color: 0x3d1f2e,
    },
    {
        id: "singalong",
        label: "Sing-Along Pavilion",
        rect: toPixels({ col: 38, row: 38, cols: 24, rows: 13 }),
        color: 0x1d3348,
    },
    {
        id: "diya",
        label: "Diya Courtyard",
        rect: toPixels({ col: 2, row: 52, cols: 24, rows: 13 }),
        color: 0x40301c,
    },
    {
        id: "tribute",
        label: "Tribute Wall",
        rect: toPixels({ col: 38, row: 52, cols: 24, rows: 27 }),
        color: 0x1e3844,
    },
    {
        id: "radio",
        label: "Radio Garden",
        rect: toPixels({ col: 2, row: 66, cols: 24, rows: 13 }),
        color: 0x223c26,
    },
    {
        id: "memory_lane",
        label: "Memory Lane",
        rect: toPixels({ col: 27, row: 23, cols: 10, rows: 57 }),
        color: 0x2a2338,
    },
    {
        id: "entry",
        label: "Entry Gate",
        rect: toPixels({ col: 1, row: 80, cols: 62, rows: 3 }),
        color: 0x2b2340,
    },
];
/** The stage platform players cannot climb onto. */
export const STAGE_PLATFORM = toPixels({ col: 16, row: 1, cols: 32, rows: 12 });
/**
 * The bamboo arch the stage is dressed with, drawn from
 * `client/public/art/stage-arch.png`.
 *
 * 416 tall is every pixel of headroom the stage has: feet on the front edge of
 * the platform, finial at y=0, which is where the map stops and the camera is
 * bounded. Its opening is at x 811-1237, y 151-416 in world pixels, and
 * `STAGE_SCREEN` is sized to hang inside that.
 *
 * Not a collider. The platform underneath it already is, and nobody can walk
 * far enough upstage to reach a leg.
 */
export const STAGE_ARCH = { x: 672, y: 0, w: 705, h: 416 };
/**
 * The big screen on the stage. Phase 3 mounts the video here.
 *
 * 352x198 is exactly 16:9, so real footage plays without cropping. It hangs
 * inside the arch's opening rather than filling the platform: the arch's
 * pediment needs the top third of the stage and there is no other headroom in
 * the map for it.
 *
 * Shrinking it costs less than it looks. This rect is the backdrop you see
 * from down Memory Lane; walk into the stage zone and `StageScreen` morphs to
 * a viewport-sized focused panel that ignores it entirely, and that is what
 * anyone actually watches the tribute on.
 *
 * The video is a DOM layer above the canvas, so anything in-world overlapping
 * this rect is hidden behind it, and the 62px down to the closest a player can
 * stand keeps name labels clear.
 */
export const STAGE_SCREEN = { x: 848, y: 156, w: 352, h: 198 };
/**
 * The Memories Theatre's screen.
 *
 * ## Why this one is tall
 *
 * Twenty-three of the twenty-eight videos fans uploaded to zubeengarg.xyz are
 * 720x1280 — phone reels, shot vertically, because that is how people film a
 * concert they are standing at. A 16:9 screen shows those as a sliver between
 * two black slabs taking up most of the wall.
 *
 * So the Theatre's screen is 9:16 and the room is built around it: the screen
 * stands in the MIDDLE and the audience sits down either side of it, which is
 * the seating a tall narrow picture actually wants. The five landscape clips
 * letterbox inside it instead, which is the minority case taking the hit.
 *
 * It is a separate rect rather than a second use of STAGE_SCREEN because the
 * two are on screen at once for anyone standing between the rooms, because the
 * stage is a synchronised broadcast while this is a screening someone chooses
 * to start, and now because they are not even the same shape.
 */
export const THEATRE_SCREEN = { x: 1510, y: 816, w: 180, h: 320 };
/**
 * The Sing-Along Pavilion's screen.
 *
 * Where the lyric video plays, so the words are in front of the people
 * singing them. Same size as the Memories Theatre's, and like it, anchored at
 * the front of the room with the seats behind — the pavilion's rows used to
 * start at y 1338 and would have sat the audience across a screen ending at
 * 1383.
 */
export const SINGALONG_SCREEN = { x: 1480, y: 1248, w: 240, h: 135 };
/**
 * The tribute wall surface that approved tributes cycle on.
 *
 * Centred in its room. It used to sit at col 50, which is the right-hand half
 * of a room spanning cols 38-62 — so the card hung off-centre in an otherwise
 * empty hall with nothing behind it. It is now the inner panel of the carved
 * board in `art/shrine.ts`, which is what the card is drawn on top of.
 */
export const TRIBUTE_WALL = toPixels({ col: 45, row: 58, cols: 11, rows: 2 });
/**
 * The Diya Courtyard's plinth, which his portrait stands on.
 *
 * Named so the renderer can recognise it and decline to paint stone over it:
 * every other collider is drawn as tinted wall, and this one's whole visual is
 * the framed portrait in `art/shrine.ts`. It stays solid — you cannot walk
 * through the centrepiece — it simply has no stone of its own.
 */
export const DIYA_PLINTH = toPixels({ col: 12, row: 56, cols: 4, rows: 3 });
/** The tribute tree's trunk. Its art is the tree drawn over it. */
export const TRIBUTE_WALL_BLOCK = toPixels({ col: 49, row: 65, cols: 2, rows: 2 });
/**
 * What the tribute room's seating faces.
 *
 * Not a screen — the tree. `rowSeats` centres its rows under whatever it is
 * given and starts them just below it, which is exactly the relationship the
 * seats need to the tree, so the tree's canopy is described here as a rect for
 * that one purpose.
 */
export const TRIBUTE_FOCUS = { x: 1440, y: 1980, w: 320, h: 200 };
/** Where a player spawns on join, in pixels: the gate, mid-arch. */
export const SPAWN_POINT = { x: 31.5 * TILE_SIZE, y: 81.5 * TILE_SIZE };
/**
 * Solid geometry. Order is authoring order; the resolver tests all of them,
 * so overlaps are harmless.
 */
const COLLIDER_TILES = [
    // Outer boundary.
    { col: 0, row: 0, cols: MAP_COLS, rows: 1 },
    { col: 0, row: MAP_ROWS - 1, cols: MAP_COLS, rows: 1 },
    { col: 0, row: 0, cols: 1, rows: MAP_ROWS },
    { col: MAP_COLS - 1, row: 0, cols: 1, rows: MAP_ROWS },
    // Stage platform (includes the screen, which is mounted on it).
    { col: 16, row: 1, cols: 32, rows: 12 },
    // Speaker stacks flanking the stage.
    { col: 13, row: 9, cols: 3, rows: 4 },
    { col: 48, row: 9, cols: 3, rows: 4 },
    // Pillars in the audience floor.
    { col: 8, row: 17, cols: 2, rows: 2 },
    { col: 54, row: 17, cols: 2, rows: 2 },
    // The back wall of the stage floor. Only the ten-tile corridor mouth
    // (cols 27-36) is open, which is what makes Memory Lane the way through
    // rather than one route among several.
    { col: 1, row: 23, cols: 26, rows: 1 },
    { col: 37, row: 23, cols: 26, rows: 1 },
    // West side of Memory Lane, with three-tile doorways into Prayer (29-31),
    // Phool Arpan (43-45), Diya Courtyard (57-59) and Radio Garden (71-73).
    { col: 26, row: 24, cols: 1, rows: 5 },
    { col: 26, row: 32, cols: 1, rows: 11 },
    { col: 26, row: 46, cols: 1, rows: 11 },
    { col: 26, row: 60, cols: 1, rows: 11 },
    { col: 26, row: 74, cols: 1, rows: 6 },
    // East side, with doorways into Memories Theatre (29-31), Sing-Along
    // Pavilion (43-45) and the Tribute Wall hall (57-59).
    { col: 37, row: 24, cols: 1, rows: 5 },
    { col: 37, row: 32, cols: 1, rows: 11 },
    { col: 37, row: 46, cols: 1, rows: 11 },
    { col: 37, row: 60, cols: 1, rows: 20 },
    // Walls between the stacked rooms on each side.
    { col: 1, row: 37, cols: 26, rows: 1 },
    { col: 1, row: 51, cols: 26, rows: 1 },
    { col: 1, row: 65, cols: 26, rows: 1 },
    { col: 37, row: 37, cols: 26, rows: 1 },
    { col: 37, row: 51, cols: 26, rows: 1 },
    // Rooms are sealed from the gate plaza, so arrivals walk up the lane.
    { col: 1, row: 79, cols: 26, rows: 1 },
    { col: 37, row: 79, cols: 26, rows: 1 },
    /*
     * The tribute tree's trunk.
     *
     * It used to be the carved board that stood here — eleven tiles wide and six
     * deep. A tree is not a wall: the only solid part is the trunk, and the
     * canopy is overhead, so a collider the width of the old board would have
     * been an invisible barrier across the middle of the room.
     */
    { col: 49, row: 65, cols: 2, rows: 2 },
    // Diya courtyard centrepiece plinth. Its art is the portrait; see DIYA_PLINTH.
    { col: 12, row: 56, cols: 4, rows: 3 },
    // Entry gate arch pillars, set either side of the lane mouth.
    { col: 28, row: 81, cols: 2, rows: 2 },
    { col: 34, row: 81, cols: 2, rows: 2 },
];
/**
 * The Memory Gallery's six frames.
 *
 * ## Why this lives in shared rather than in the renderer
 *
 * The pictures are solid — you cannot walk over somebody's photograph — which
 * means the client has to DRAW a frame exactly where the server will BLOCK
 * one. Two copies of that layout is the same trap the seating fell into, and
 * the failure is worse here: a frame drawn where nothing blocks lets fans walk
 * through the pictures, and a block where nothing is drawn is an invisible
 * wall in the middle of a room.
 *
 * So the positions are computed once, here, and both sides read them.
 */
const GALLERY = ZONES.find((z) => z.id === "prayer").rect;
/** Frame size in world pixels. The sprite is 300x227, so the height follows. */
export const GALLERY_FRAME_SIZE = {
    w: 150,
    h: Math.round((150 * 227) / 300),
};
/** Where each of the six frames hangs, as its centre. */
export const GALLERY_FRAMES = (() => {
    const COLS = 3;
    const ROWS = 2;
    const MARGIN_X = 44;
    const TOP = 50;
    const ROW_PAD = 44;
    const stepX = (GALLERY.w - MARGIN_X * 2 - GALLERY_FRAME_SIZE.w) / (COLS - 1);
    const firstRow = GALLERY.y + TOP + GALLERY_FRAME_SIZE.h / 2;
    const rowGap = GALLERY_FRAME_SIZE.h + ROW_PAD;
    const out = [];
    for (let r = 0; r < ROWS; r += 1) {
        for (let c = 0; c < COLS; c += 1) {
            out.push({
                x: GALLERY.x + MARGIN_X + GALLERY_FRAME_SIZE.w / 2 + c * stepX,
                y: firstRow + r * rowGap,
            });
        }
    }
    return out;
})();
/**
 * What each frame blocks.
 *
 * Inset from the frame it belongs to, so the gilt edge overhangs its own solid
 * mass by a few pixels — standing right against a picture should put you at
 * its edge, not floating a frame's width away from it. The gaps left between
 * them are wide enough to walk: 44px between the rows and over 100 between the
 * columns, against a player half-width of 14.
 */
export const GALLERY_BLOCKS = GALLERY_FRAMES.map((f) => ({
    x: f.x - GALLERY_FRAME_SIZE.w / 2 + 7,
    y: f.y - GALLERY_FRAME_SIZE.h / 2 + 7,
    w: GALLERY_FRAME_SIZE.w - 14,
    h: GALLERY_FRAME_SIZE.h - 14,
}));
/**
 * Where the Radio Garden's tea stall stands.
 *
 * The stall is drawn by the renderer, but it has to be SOLID, and those are
 * two files. Working the position out twice is how the seating and the gallery
 * frames both ended up hanging in mid-air, so it is worked out once here and
 * the renderer reads it.
 */
const RADIO_ROOM = ZONES.find((z) => z.id === "radio").rect;
export const TEA_STALL = {
    /** The stall's centre line: everything in it is drawn either side of this. */
    x: RADIO_ROOM.x + 268,
    /** The floor line, where the posts meet the ground. */
    groundY: RADIO_ROOM.y + RADIO_ROOM.h - 96,
};
/**
 * The part of the stall you cannot walk through.
 *
 * Not the whole elevation — the roof is over your head and the sky above it is
 * not a wall. What stops you is the body of it: the back shelf, the counter in
 * front of that, and the two posts holding the tin up — the band from the top
 * of the shelf down to the floor line. Stopping at the counter alone was not
 * enough, because the strip behind it is where the shelf, the tumblers and the
 * radio set are, and a dog walked through the middle of them.
 * Until this existed the stall was scenery you strolled straight through,
 * along with the teapot and the radio set standing on it.
 */
export const TEA_STALL_BLOCK = {
    x: TEA_STALL.x - 158,
    y: TEA_STALL.groundY - 104,
    w: 324,
    h: 104,
};
export const COLLIDERS = [
    ...COLLIDER_TILES.map(toPixels),
    ...GALLERY_BLOCKS,
    TEA_STALL_BLOCK,
];
/**
 * Colliders the renderer must NOT paint as stone.
 *
 * Every other collider in the venue is a wall and is drawn as one. These two
 * are solid because you should not be able to walk through a centrepiece, but
 * they already have art of their own — the framed portrait and the tribute
 * board — and painting tinted stone over the top put a blue-grey slab across
 * the middle of both. Keeping the list here rather than in the renderer means
 * the collider and the decision not to paint it cannot drift apart.
 */
export const UNPAINTED_COLLIDERS = [
    DIYA_PLINTH,
    TRIBUTE_WALL_BLOCK,
    // Each gallery frame is drawn by the room; stone over it would hide a photo.
    ...GALLERY_BLOCKS,
    // The stall is painted in full by the Radio Garden, counter and all.
    TEA_STALL_BLOCK,
];
/** True when this collider is drawn by the room rather than as a wall. */
export function isUnpainted(rect) {
    return UNPAINTED_COLLIDERS.some((u) => u.x === rect.x && u.y === rect.y && u.w === rect.w && u.h === rect.h);
}
/** Half-extents of a player's collision box, in pixels. */
export const PLAYER_HALF_W = 10;
export const PLAYER_HALF_H = 8;
function overlaps(cx, cy, rect) {
    return (cx + PLAYER_HALF_W > rect.x &&
        cx - PLAYER_HALF_W < rect.x + rect.w &&
        cy + PLAYER_HALF_H > rect.y &&
        cy - PLAYER_HALF_H < rect.y + rect.h);
}
/** True when a player centred at (x, y) would be inside solid geometry. */
export function isBlocked(x, y) {
    for (const rect of COLLIDERS) {
        if (overlaps(x, y, rect))
            return true;
    }
    return false;
}
/**
 * Move a player by (dx, dy) pixels, resolving each axis independently so that
 * sliding along a wall works instead of sticking. Identical on client and server.
 */
export function resolveMove(x, y, dx, dy) {
    let nextX = x;
    let nextY = y;
    if (dx !== 0) {
        const candidate = clampX(x + dx);
        if (!isBlocked(candidate, nextY))
            nextX = candidate;
    }
    if (dy !== 0) {
        const candidate = clampY(y + dy);
        if (!isBlocked(nextX, candidate))
            nextY = candidate;
    }
    return { x: nextX, y: nextY };
}
function clampX(x) {
    return Math.min(Math.max(x, PLAYER_HALF_W), MAP_WIDTH - PLAYER_HALF_W);
}
function clampY(y) {
    return Math.min(Math.max(y, PLAYER_HALF_H), MAP_HEIGHT - PLAYER_HALF_H);
}
/** Returns the zone containing (x, y), or null when between zones. */
export function zoneAt(x, y) {
    for (const zone of ZONES) {
        const r = zone.rect;
        if (x >= r.x && x <= r.x + r.w && y >= r.y && y <= r.y + r.h)
            return zone;
    }
    return null;
}
/** Shortest distance from (x, y) to a rect's edge; 0 when inside. */
export function distanceToRect(x, y, rect) {
    const dx = Math.max(rect.x - x, 0, x - (rect.x + rect.w));
    const dy = Math.max(rect.y - y, 0, y - (rect.y + rect.h));
    return Math.hypot(dx, dy);
}
/** How close you must be to a seat to take it. */
export const SEAT_REACH = 72;
/** Radius from a table centre to the seats around it. */
const TABLE_RADIUS = 46;
function rowSeats(zone, rect, rows, perRow, screen) {
    /*
     * An audience, not a scattering.
     *
     * The rows used to span the full width of the room — 628px of seats facing a
     * 240px screen — so the end seats looked at it from forty-five degrees and
     * the back row sat a room's length away. Anyone who sat down could not see
     * the video they had sat down for.
     *
     * With a screen given, the rows are centred under it, no wider than it plus
     * a little, and start just below it. Without one they fall back to the old
     * full-width layout, which is right for a room with nothing to look at.
     */
    const width = screen ? Math.min(rect.w - 120, screen.w + 150) : rect.w - 140;
    const centre = screen ? screen.x + screen.w / 2 : rect.x + rect.w / 2;
    const left = centre - width / 2;
    const right = centre + width / 2;
    const top = screen ? screen.y + screen.h + 54 : rect.y + 122;
    const out = [];
    for (let r = 0; r < rows; r++) {
        for (let c = 0; c < perRow; c++) {
            const x = left + (c * (right - left)) / (perRow - 1);
            const y = top + r * 48;
            // Rows face the front of the room, which is where the screen hangs.
            out.push({ id: `${zone}-r${r}c${c}`, x, y, facing: "up", zone, tableId: null });
        }
    }
    return out;
}
/**
 * Two blocks of seats flanking a screen, facing in across it.
 *
 * For the Memories Theatre, whose screen is vertical. Rows in front of a tall
 * narrow picture waste the whole room to its left and right and push the back
 * row a long way off; seating down both sides puts everybody at the same short
 * distance, side-on to a picture that is taller than it is wide.
 *
 * Columns are laid nearest-first outward, so the closest seats fill first and
 * a half-empty theatre still looks like an audience rather than a fringe.
 */
function sideSeats(zone, screen, cols, rows) {
    /** Gap from the screen edge to the first column, and between columns. */
    const STEP = 92;
    /** Vertical inset so no seat is level with the very top or bottom edge. */
    const INSET = 40;
    const top = screen.y + INSET;
    const bottom = screen.y + screen.h - INSET;
    const out = [];
    for (const side of [-1, 1]) {
        for (let c = 0; c < cols; c++) {
            // Nearest column first, then stepping away from the screen.
            const x = side < 0 ? screen.x - STEP * (c + 1) : screen.x + screen.w + STEP * (c + 1);
            for (let r = 0; r < rows; r++) {
                const y = rows === 1 ? (top + bottom) / 2 : top + (r * (bottom - top)) / (rows - 1);
                out.push({
                    id: `${zone}-${side < 0 ? "l" : "r"}${c}r${r}`,
                    x,
                    y,
                    // Looking in at the screen, which is the whole reason for the chair.
                    facing: side < 0 ? "right" : "left",
                    zone,
                    tableId: null,
                });
            }
        }
    }
    return out;
}
function tableSeats(table) {
    // Four chairs, one per side, each facing the table.
    const offsets = [
        [0, -TABLE_RADIUS, "down"],
        [0, TABLE_RADIUS, "up"],
        [-TABLE_RADIUS, 0, "right"],
        [TABLE_RADIUS, 0, "left"],
    ];
    return offsets.map(([dx, dy, facing], i) => ({
        id: `${table.id}-s${i}`,
        x: table.x + dx,
        y: table.y + dy,
        facing,
        zone: table.zone,
        tableId: table.id,
    }));
}
const SINGALONG = ZONES.find((z) => z.id === "singalong").rect;
const TRIBUTE = ZONES.find((z) => z.id === "tribute").rect;
/**
 * Tables to gather around.
 *
 * A table is the only piece of furniture here that puts four strangers in a
 * circle looking at each other, which is most of what a gathering is. They go
 * in the Radio Garden and the Tribute Wall hall — the two rooms people linger
 * in rather than face a screen.
 *
 * Not the gate plaza, which is where they were first put: the plaza is three
 * tiles tall, and a four-seat ring needs `TABLE_RADIUS` of clearance above and
 * below the table plus the player's own half-height. The north seat landed
 * inside the wall that seals the rooms off from the plaza, and anyone who took
 * it was pinned against geometry they could not walk out of. The seat test
 * asserts this cannot come back.
 */
export const TABLES = [
/*
 * The Radio Garden's three tables were removed.
 *
 * It is a garden: bamboo, water, grass and a rhino, with the dial under the
 * trees. Twelve dining chairs in the middle of it made it read as a
 * courtyard cafe, and a garden is somewhere you stand and listen rather than
 * somewhere you are seated. The Tribute Wall hall keeps its two, because
 * that IS a room people sit down in to write something.
 */
/*
 * No tables in the tribute room either.
 *
 * It had two, ringed with four chairs each, from when the room was a hall
 * with a board on the wall. It is a tree now, and people do not sit in
 * circles facing each other under a memorial tree — they sit facing it, the
 * way the Sing-Along Pavilion sits facing its screen. See `SEATS`.
 */
];
/** Every seat in the venue. Row seating in the two halls, plus every table. */
export const SEATS = [
    // Rows facing the tribute tree, laid out exactly as the Pavilion's are:
    // three rows of five, centred under what they are looking at.
    ...rowSeats("tribute", TRIBUTE, 3, 5, TRIBUTE_FOCUS),
    // The Theatre seats down both sides of a vertical screen; the Pavilion still
    // seats in rows, because a lyric video is 16:9 and wants an audience in
    // front of it. Two halls, two shapes of picture, two shapes of audience.
    ...sideSeats("theatre", THEATRE_SCREEN, 2, 4),
    ...rowSeats("singalong", SINGALONG, 3, 5, SINGALONG_SCREEN),
    ...TABLES.flatMap(tableSeats),
];
const SEAT_BY_ID = new Map(SEATS.map((seat) => [seat.id, seat]));
export function seatById(id) {
    return SEAT_BY_ID.get(id);
}
export function seatsInZone(zone) {
    return SEATS.filter((seat) => seat.zone === zone);
}
/** The nearest seat within reach of (x, y), or null. */
export function nearestSeat(x, y, taken) {
    let best = null;
    let bestDist = SEAT_REACH;
    for (const seat of SEATS) {
        if (taken.has(seat.id))
            continue;
        const dist = Math.hypot(seat.x - x, seat.y - y);
        if (dist <= bestDist) {
            best = seat;
            bestDist = dist;
        }
    }
    return best;
}
//# sourceMappingURL=venue.js.map